import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEBUG_ENABLED_KEY, DEBUG_MAX_ENTRIES, loadDebugEntries, saveDebugEntries,
  type PriceTagDebugEntry, type PriceTagDebugEvent, type PriceTagDebugObserver,
  debugUploadPayload, debugError, type DebugUpload,
} from "./debug";

export function usePriceTagDebug(upload?: DebugUpload, connected = false) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(DEBUG_ENABLED_KEY) === "true"; } catch { return false; }
  });
  const [entries, setEntries] = useState(loadDebugEntries);
  const entriesRef = useRef(entries);
  const enabledRef = useRef(enabled);
  const [frame, setFrame] = useState<{ id: string; dataUrl: string } | null>(null);
  const [storageFailed, setStorageFailed] = useState(false);
  const uploadBusyRef = useRef(false);
  const rejectedRevisionsRef = useRef(new Map<string, number>());

  const commit = useCallback((next: PriceTagDebugEntry[]) => {
    entriesRef.current = next;
    setEntries(next);
    setStorageFailed(!saveDebugEntries(next));
  }, []);

  const toggle = useCallback((next: boolean) => {
    enabledRef.current = next;
    setEnabled(next);
    try { localStorage.setItem(DEBUG_ENABLED_KEY, String(next)); } catch { /* Session-only preference. */ }
  }, []);

  const record = useCallback((id: string | undefined, event: PriceTagDebugEvent) => {
    if (!id || !entriesRef.current.some((entry) => entry.id === id)) return;
    commit(entriesRef.current.map((entry) => entry.id === id
      ? { ...entry, events: [...entry.events, event] } : entry));
  }, [commit]);

  const begin = useCallback((storeId?: string): { id: string; observer: PriceTagDebugObserver } | undefined => {
    if (!enabledRef.current) return undefined;
    const id = crypto.randomUUID();
    const entry: PriceTagDebugEntry = {
      id, timestamp: new Date().toISOString(), storeId,
      userAgent: navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight, pixelRatio: window.devicePixelRatio },
      events: [],
    };
    setFrame(null);
    commit([entry, ...entriesRef.current].slice(0, DEBUG_MAX_ENTRIES));
    return {
      id,
      observer: {
        event: (event) => record(id, event),
        frame: (dataUrl) => setFrame({ id, dataUrl }),
      },
    };
  }, [commit, record]);

  const clear = useCallback(() => { commit([]); setFrame(null); }, [commit]);

  useEffect(() => {
    if (!enabled || !connected || !upload) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const flush = async () => {
      if (cancelled) return;
      if (uploadBusyRef.current) { timer = setTimeout(() => void flush(), 5000); return; }
      uploadBusyRef.current = true;
      try {
        // Oldest first, at most three snapshots per tick; one RPC at a time.
        const pending = [...entriesRef.current].reverse().filter((entry) =>
          entry.events.length > (entry.syncedRevision ?? 0) &&
          rejectedRevisionsRef.current.get(entry.id) !== entry.events.length,
        ).slice(0, 3);
        for (const entry of pending) {
          if (cancelled) break;
          const revision = entry.events.length;
          try {
            const ack = await upload(debugUploadPayload(entry));
            if (!ack.recorded || ack.attemptId !== entry.id || ack.revision !== revision) throw new Error("Invalid debug upload acknowledgement");
            if (!cancelled) commit(entriesRef.current.map((item) => item.id === entry.id
              ? { ...item, syncedRevision: revision, uploadError: undefined } : item));
          } catch (error) {
            if (cancelled) break;
            const detail = debugError(error);
            if (["INVALID_REQUEST", "METHOD_NOT_ALLOWED"].includes(detail.name)) rejectedRevisionsRef.current.set(entry.id, revision);
            commit(entriesRef.current.map((item) => item.id === entry.id
              ? { ...item, uploadError: `${detail.name}: ${detail.message}` } : item));
            break; // Retry transient failures without delaying recognition.
          }
        }
      } finally {
        uploadBusyRef.current = false;
        if (!cancelled) timer = setTimeout(() => void flush(), 5000);
      }
    };
    timer = setTimeout(() => void flush(), 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled, connected, upload, commit]);

  return { enabled, toggle, entries, begin, record, frame, clear, storageFailed };
}
